/** Матрица «По дням»: столбцы, текст ячеек, видимые строки, цель окна графика. Чистые функции.
 *
 *  Все числа (дни, итоги недель и месяца, Δ) считает бэкенд (`mp_finance/daily.py`); здесь — только
 *  показ. Знаки — соглашение `calc.ts`: значения приходят в знаке витрины, `kind = expense`
 *  (удержания, расходы, отмены, себестоимость, ДРР) показывается с обратным знаком — расходом без
 *  минуса, Δ — тоже по показанному числу, тон — по смыслу (рост расхода — красный). null — пусто;
 *  у строк от себестоимости (`row.cogs`: себестоимость, расходы, маржа, маржа %) null дня / итога,
 *  начатого раньше `cogs_from` (или без цен), — «—» с подсказкой «себестоимость считается с …»
 *  (`CogsGap`), а не пусто: маржа там не «ещё не пришла», её нет. */
import type { DailyRow, MpDaily } from "@/api/mpAnalytics";
import type { OzFilters } from "@/api/ozonAnalytics";
import { deltaTone, shown, shownTitle, type Tone } from "./calc";
import { cogsFromText, cogsNullTitle } from "./cogs";
import { PP_EPS } from "./compact";
import { int, MONTH_NAMES, pct, pctSigned, pp } from "./format";
import type { ItemTrendTarget } from "./ItemTrendDialog";

/** Порог шума относительной Δ: 0,05 %. */
const REL_EPS = 0.0005;

const WEEKDAYS = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];

export type Column =
  | { type: "day"; idx: number; date: string; inMonth: boolean }
  | { type: "week"; w: number; field: "total" | "d_day" | "d_week" }
  | { type: "month" };

/** Столбцы: дни оси, после последнего дня каждой недели — «Неделя N», «Δ дн%», «Δ нед%»; в конце —
 *  итог месяца. Неделя без своего последнего дня на оси — нарушение контракта бэка. */
export function matrixColumns(data: Pick<MpDaily, "days" | "weeks" | "month">): Column[] {
  const ends = new Map(data.weeks.map((w, i) => [w.to, i]));
  const seen = new Set<number>();
  const cols: Column[] = [];
  data.days.forEach((date, idx) => {
    cols.push({ type: "day", idx, date, inMonth: date.startsWith(data.month) });
    const w = ends.get(date);
    if (w !== undefined) {
      seen.add(w);
      cols.push({ type: "week", w, field: "total" });
      cols.push({ type: "week", w, field: "d_day" });
      cols.push({ type: "week", w, field: "d_week" });
    }
  });
  if (seen.size !== data.weeks.length) {
    throw new Error("матрица «По дням»: конец недели вне оси дней — ответ бэка разошёлся с контрактом");
  }
  cols.push({ type: "month" });
  return cols;
}

/** «Пн 31.08» — заголовок дня. */
export function dayHead(date: string): { weekday: string; day: string } {
  const d = new Date(`${date}T00:00:00Z`);
  return { weekday: WEEKDAYS[d.getUTCDay()]!, day: `${date.slice(8, 10)}.${date.slice(5, 7)}` };
}

/** «Итог сентябрь» из "2026-09". */
export function monthTotalHead(month: string): string {
  return `Итог ${MONTH_NAMES[Number(month.slice(5, 7)) - 1]!.toLowerCase()}`;
}

export interface Cell {
  text: string;
  /** negative — доход с минусом (красным); Tone — Δ; null — обычный цвет */
  tone: Tone | "negative" | null;
  title: string | null;
}

const EMPTY: Cell = { text: "", tone: null, title: null };

/** Значение строки (день, итог недели или месяца): по показанному числу, ₽ и шт. — целыми, доли — %. */
export function valueCell(row: DailyRow, v: number | null): Cell {
  if (v === null) return EMPTY;
  const s = shown(v, row.kind);
  const text = row.unit === "pct" ? pct(s) : int(s);
  return {
    text,
    tone: row.kind === "income" && s < (row.unit === "pct" ? -REL_EPS : -0.5) ? "negative" : null,
    title: shownTitle(s, row.kind),
  };
}

/** Δ строки: у сумм — «+12,3%», у долей — «+1,2 п.п.»; по показанному числу, тон по смыслу. */
export function deltaCell(row: DailyRow, d: number | null): Cell {
  if (d === null) return EMPTY;
  const s = shown(d, row.kind);
  const isPct = row.unit === "pct";
  return {
    text: isPct ? pp(s) : pctSigned(s),
    tone: deltaTone(s, isPct ? PP_EPS : REL_EPS, row.kind),
    title: null,
  };
}

/** Где у строк от себестоимости её нет: `why` — подсказка «—», `from` — `cogs_from` (null или
 *  `ok = false` — себестоимости нет ни в одном дне), `dataTo` — последний день с финансами (позже —
 *  данных нет: пусто, а не «—»), начала недель и месяца — для итогов. */
export interface CogsGap {
  ok: boolean;
  from: string | null;
  why: string;
  dataTo: string | null;
  weekFrom: string[];
  monthFrom: string;
}

export function cogsGapOf(data: Pick<MpDaily, "cogs" | "spans" | "weeks" | "month_from">): CogsGap {
  const ok = data.cogs.status === "ok" && data.cogs.cogs_from !== null;
  return {
    ok,
    from: data.cogs.cogs_from,
    why: ok ? cogsFromText(data.cogs.cogs_from) : cogsNullTitle(data.cogs, "площадки"),
    dataTo: data.spans.finance.to,
    weekFrom: data.weeks.map((w) => w.from),
    monthFrom: data.month_from,
  };
}

/** null строки себестоимости за период, начатый с `start`: «—» с причиной, если период начат
 *  раньше `cogs_from` (или цен нет) и в нём есть дни с данными; иначе — пусто. */
function cogsDash(row: DailyRow, start: string, gap: CogsGap | null): Cell {
  if (!row.cogs || !gap || gap.dataTo === null || start > gap.dataTo) return EMPTY;
  if (gap.ok && gap.from !== null && start >= gap.from) return EMPTY;
  return { text: "—", tone: null, title: gap.why };
}

/** Ячейка строки в столбце. Нет значения дня / итога недели в строке — нарушение контракта бэка
 *  (ошибка, а не пустая ячейка: пусто значит «данных нет»). `weekTo` — последний день недели
 *  (Δ дн не за него — подсказка, за какой день); `gap` — где нет себестоимости («—» у строк от неё). */
export function cellOf(row: DailyRow, col: Column, weekTo?: string, gap: CogsGap | null = null): Cell {
  if (col.type === "day") {
    const v = row.days[col.idx];
    if (v === undefined) {
      throw new Error(`строка «${row.label}»: нет значения дня ${col.date} — ответ бэка разошёлся с контрактом`);
    }
    return v === null ? cogsDash(row, col.date, gap) : valueCell(row, v);
  }
  if (col.type === "month") {
    return row.month === null && gap ? cogsDash(row, gap.monthFrom, gap) : valueCell(row, row.month);
  }
  const w = row.weeks[col.w];
  if (!w) throw new Error(`строка «${row.label}»: нет итога недели ${col.w + 1}`);
  if (col.field === "total") {
    const from = gap?.weekFrom[col.w];
    return w.total === null && gap && from !== undefined
      ? cogsDash(row, from, gap)
      : valueCell(row, w.total);
  }
  const d = w[col.field];
  // Δ строки от себестоимости, база которой раньше `cogs_from`, — «—» с той же подсказкой, что у
  // значений (неделя без дней с данными, `d_day_at` null, — пусто)
  if (d === null && gap && row.cogs && w.d_day_at !== null) {
    const weekFrom = gap.weekFrom[col.w];
    const base =
      col.field === "d_day" ? shiftDay(w.d_day_at, -1) : weekFrom ? shiftDay(weekFrom, -7) : null;
    if (base !== null) return cogsDash(row, base, gap);
  }
  const c = deltaCell(row, d);
  // неполная неделя (конец месяца или данных): Δ нед — к тем же дням прошлой недели
  if (col.field === "d_week" && c.text && w.n_days < 7) {
    return { ...c, title: `к тем же ${w.n_days} дн. прошлой недели` };
  }
  // текущая неделя: Δ дн — последний день с данными, а не конец недели
  if (col.field === "d_day" && c.text && w.d_day_at && w.d_day_at !== weekTo) {
    return { ...c, title: `${ddmm(w.d_day_at)} (последний день с данными) к предыдущему дню` };
  }
  return c;
}

const ddmm = (iso: string): string => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;

/** ISO-дата ± дней (UTC). */
function shiftDay(iso: string, days: number): string {
  const t = new Date(`${iso}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + days);
  return t.toISOString().slice(0, 10);
}

/** Видимые строки: статья — только под раскрытой группой (`open` — ключи строк групп). */
export function visibleRows(rows: DailyRow[], open: ReadonlySet<string>): DailyRow[] {
  return rows.filter((r) => r.parent === null || open.has(r.parent));
}

/** У строки есть статьи (раскрытие). */
export function childCount(rows: DailyRow[], key: string): number {
  return rows.filter((r) => r.parent === key).length;
}

/** Фильтры окна графика: месяц вкладки, без сравнения, «общие» отдельной строкой. */
export function monthFilters(data: Pick<MpDaily, "month_from" | "month_to">, legal: string | null): OzFilters {
  return {
    date_from: data.month_from,
    date_to: data.month_to,
    cmp_from: null,
    cmp_to: null,
    legal,
    alloc: false,
  };
}

/** Цель окна графика строки P&L (группа, статья, ДРР ₽ — группа рекламы): итоги месяца в знаке
 *  витрины, как у строки сводки за месяц. null — не строка P&L, справочная или месяц пуст. Итоги
 *  строк P&L месяца считаются по одним дням: у группы есть итог, а у её статьи или выручки нет —
 *  нарушение контракта бэка (ошибка, а не 0 в окне). */
export function chartTargetOf(data: MpDaily, row: DailyRow): ItemTrendTarget | null {
  if (row.grp === null || row.reference || row.month === null) return null;
  const gkey = `grp:${row.grp}`;
  const group = data.rows.find((r) => r.key === gkey);
  if (!group || group.month === null) return null;
  const contract = (what: string): Error =>
    new Error(`строка «${row.label}»: ${what} — ответ бэка разошёлся с контрактом`);
  const rev = data.rows.find((r) => r.key === "grp:rev");
  if (!rev) throw contract("нет строки «Выкуплено ₽» (grp:rev)");
  if (rev.month === null) throw contract("у «Выкуплено ₽» нет итога месяца, а у группы есть");
  if (row.grp_label === null) throw contract("у строки P&L нет подписи группы (grp_label)");
  return {
    grp: row.grp,
    grpLabel: row.grp_label,
    item: row.level === 1 ? row.item : null,
    cur: group.month,
    cmp: null,
    items: data.rows
      .filter((r) => r.parent === gkey)
      .map((r) => {
        if (r.item === null || r.month === null) {
          throw contract(`у статьи «${r.label}» нет ${r.item === null ? "кода статьи" : "итога месяца"}`);
        }
        return { item: r.item, cur: r.month, cmp: null };
      }),
    revCur: rev.month,
    revCmp: null,
    mart: data.mart,
  };
}
